<template>
  <div class="position-relative w-100">
    <label v-if="label" :for="id + '-base-select'" class="form-label">{{ label }}</label>

    <div role="listbox" class="w-100 position-relative">
      <input
        type="text"
        :id="id + '-base-select'"
        class="form-control"
        :value="searchOptionsQuery"
        :title="searchOptionsQuery"
        :disabled="disabled"
        :required="required"
        :aria-expanded="dropdownVisible.toString()"
        :aria-controls="id + '-dropdown'"
        :class="{ 'is-invalid': errorBorder }"
        @input="handleSearchInput"
        @focus="handleFocus"
        @blur="hideDropdown"
        @keydown.down.prevent="moveDown"
        @keydown.up.prevent="moveUp"
        @keydown.enter.prevent="selectHighlighted"
        @click="dropdownVisible = true"
      />
      <i
        class="fa fa-chevron-down dropdown-arrow"
        :class="{ active: dropdownVisible }"
      ></i>
      <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
    </div>

    <div
      v-if="dropdownVisible"
      class="card position-absolute w-100 border rounded shadow dropdown-menu-select"
      :id="id + '-dropdown'"
    >
      <div
        v-for="(option, index) in filteredOptions"
        :key="getOptionValue(option)"
        class="dropdown-item"
        :class="{ 'bg-light': index === highlightedIndex }"
        @click="handleSelectOption(option)"
        ref="dropdownItem"
      >
        <span v-html="highlightMatch(option)"></span>
      </div>
      <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-muted">
        No options found
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import escapeRegExp from 'lodash/escapeRegExp';

export default {
  name: 'BaseSelect',

  props: {
    value: null,
    options: {
      type: Array,
      required: true
    },
    disabled: Boolean,
    errorBorder: Boolean,
    errorMessage: String,
    onSelect: Function,
    defaultText: {
      type: String,
      default: 'Select'
    },
    defaultValue: {
      default: null
    },
    label: String,
    required: Boolean,
    id: {
      type: String,
      required: true
    },
    optionLabelKey: {
      type: String,
      default: 'title'
    },
    optionValueKey: {
      type: String,
      default: 'value'
    }
  },
  data() {
    return {
      selectedOption: { [this.optionLabelKey]: this.defaultText, [this.optionValueKey]: this.defaultValue },
      searchOptionsQuery: '',
      dropdownVisible: false,
      filteredOptions: [...this.options],
      highlightedIndex: -1
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        const selected = this.options.find(opt => this.getOptionValue(opt) === newVal);
        this.selectedOption = selected || {
          [this.optionLabelKey]: this.defaultText,
          [this.optionValueKey]: this.defaultValue
        };
        this.searchOptionsQuery = selected ? this.getOptionLabel(selected) : '';
      }
    },
    searchOptionsQuery(newVal) {
      if (!newVal) {
        this.filteredOptions = [...this.options];
      } else {
        this.filteredOptions = this.options.filter(option =>
          this.getOptionLabel(option).toLowerCase().includes(newVal.toLowerCase())
        );
      }
      this.highlightedIndex = 0;
    },
    options(newOptions) {
      this.filteredOptions = [...newOptions];
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    highlightMatch(option) {
      const label = this.getOptionLabel(option);
      const query = this.searchOptionsQuery;
      if (!query) return label;

      try {
        const regex = new RegExp(`(${escapeRegExp(query)})`, 'ig');
        return label.replace(regex, '<strong>$1</strong>');
      } catch {
        return label;
      }
    },
    handleFocus() {
      this.dropdownVisible = true;
      this.filteredOptions = [...this.options];
      this.highlightedIndex = 0;
    },
    hideDropdown() {
      setTimeout(() => {
        this.dropdownVisible = false;
        const selected = this.options.find(e => this.getOptionValue(e) === this.value);
        this.searchOptionsQuery = selected ? this.getOptionLabel(selected) : '';
      }, 200);
    },
    handleSelectOption(option) {
      this.selectedOption = option;
      this.searchOptionsQuery = this.getOptionLabel(option);
      this.dropdownVisible = false;
      this.$emit('input', this.getOptionValue(option));
      if (this.onSelect) this.onSelect(option);
    },
    handleSearchInput(event) {
      this.searchOptionsQuery = event.target.value;
      this.dropdownVisible = true;
      this.highlightedIndex = 0;
    },
    moveDown() {
      if (!this.dropdownVisible) {
        this.dropdownVisible = true;
        this.highlightedIndex = 0;
        return;
      }
      if (this.highlightedIndex < this.filteredOptions.length - 1) {
        this.highlightedIndex++;
        this.scrollHighlightedIntoView();
      }
    },
    moveUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
        this.scrollHighlightedIntoView();
      }
    },
    selectHighlighted() {
      if (
        this.highlightedIndex >= 0 &&
        this.highlightedIndex < this.filteredOptions.length
      ) {
        const option = this.filteredOptions[this.highlightedIndex];
        this.handleSelectOption(option);
      }
    },
    scrollHighlightedIntoView() {
      this.$nextTick(() => {
        const items = this.$refs.dropdownItem;
        const item = Array.isArray(items) ? items[this.highlightedIndex] : null;
        if (item && typeof item.scrollIntoView === 'function') {
          item.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
      });
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.hideDropdown();
      }
    },
    getOptionLabel(option) {
      return get(option, this.optionLabelKey) || '';
    },
    getOptionValue(option) {
      return get(option, this.optionValueKey);
    }
  }
};
</script>

<style scoped>
input.form-control {
  padding-right: 2.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-arrow {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%) rotate(0.5turn);
  pointer-events: none;
  transition: transform 0.3s;
  z-index: 2;
}

.dropdown-arrow.active {
  transform: translateY(-50%) rotate(0turn);
}

.dropdown-menu-select {
  z-index: 9999;
  max-height: 200px;
  overflow-y: auto;
}

.dropdown-item {
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: rgba(24, 28, 33, 0.06) !important;
}

.dark-style .dropdown-item:hover {
  background-color: #3e4147 !important;
}

.dropdown-item strong {
  font-weight: bold;
  color: #000;
}

</style>
